import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, RefreshCcw, Server, GitCommit, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const EXPECTED_VERSION = 'v2026.05.22-demo-fixes';

export default function SuperAdminVersion() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVersion = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/version`);
      setInfo(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to fetch version');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchVersion(); }, []);

  const matches = info && info.app_version === EXPECTED_VERSION;
  const features = info?.build_signature || {};
  const featureCount = Object.keys(features).length;
  const featuresOn = Object.values(features).filter(Boolean).length;

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="version-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Build & Version</h1>
          <p className="text-sm text-slate-400 mt-1">Shows exactly which backend build is running. Use this to confirm a deploy actually picked up your latest changes.</p>
        </div>
        <Button onClick={fetchVersion} disabled={loading} variant="outline" className="border-slate-600 text-slate-200" data-testid="refresh-version-btn">
          <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {error && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 text-red-200 text-sm">
            <p className="font-bold">Failed to read build info.</p>
            <p className="mt-1">{error}</p>
            <p className="mt-2 text-xs text-red-300">If you see 404, the deployed backend doesn't have the /api/version endpoint — meaning your deploy is older than v2026.05.21.</p>
          </CardContent>
        </Card>
      )}

      {info && (
        <>
          <Card className={matches ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-amber-500/5 border-amber-500/30'}>
            <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-slate-300" />
                  <div>
                    <p className="text-xs text-slate-400">Currently running</p>
                    <p className="text-lg font-bold text-white" data-testid="current-version">{info.app_version}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-slate-300" />
                  <div>
                    <p className="text-xs text-slate-400">Latest expected build</p>
                    <p className="text-lg font-bold text-white">{EXPECTED_VERSION}</p>
                  </div>
                </div>
                {matches ? (
                  <Badge className="bg-emerald-600 text-white">✓ Build matches — production is up to date</Badge>
                ) : (
                  <Badge className="bg-amber-600 text-white">⚠ Build mismatch — redeploy needed</Badge>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <GitCommit className="w-5 h-5 text-slate-300" />
                  <div>
                    <p className="text-xs text-slate-400">Git SHA</p>
                    <p className="text-base font-mono text-white" data-testid="git-sha">{info.git_sha}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-300" />
                  <div>
                    <p className="text-xs text-slate-400">Process started</p>
                    <p className="text-sm text-white">{new Date(info.process_started_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Feature Checklist</h2>
                <Badge variant="outline" className="border-slate-600 text-slate-200">{featuresOn} / {featureCount} present</Badge>
              </div>
              <p className="text-xs text-slate-400 mb-4">
                Every checkbox below maps to a behavior that should be live. If any are missing on production, your deploy is stale.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(features).map(([key, on]) => (
                  <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded ${on ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/30'}`}>
                    {on ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                    <span className={`text-xs font-mono ${on ? 'text-slate-200' : 'text-red-200'}`}>{key}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-cyan-500/5 border-cyan-500/30">
            <CardContent className="p-5 text-sm text-cyan-100 space-y-2">
              <p className="font-bold text-cyan-200">How to read this</p>
              <ul className="list-disc list-inside space-y-1 text-cyan-100/90">
                <li>If <code className="text-white">currently running</code> matches <code className="text-white">latest expected build</code>, you're up to date.</li>
                <li>If they differ, the deploy didn't pick up the latest code — trigger a fresh deploy.</li>
                <li>The Git SHA helps you cross-check exactly which commit is live on Emergent.</li>
                <li>Open this page on production and on preview side by side — if the version strings differ, you have a deploy issue, not a code issue.</li>
              </ul>
              <p className="text-xs text-cyan-300 mt-2">Tip: Try <code className="text-white">curl https://recruitizr.ai/api/version</code> on your own machine for a script-friendly check.</p>
            </CardContent>
          </Card>
        </>
      )}

      {!info && !error && !loading && (
        <Card className="bg-slate-900 border-slate-700">
          <CardContent className="p-5 text-slate-400 text-sm">No data.</CardContent>
        </Card>
      )}
    </div>
  );
}
